import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { createQuery } from '../query.ts'

/**
 * 레시피 v2 템플릿 — `GET /templates/{kind}` · 표준 레시피 `GET /recipes/standard`(선행대장 `AH-145`).
 *
 * 🔴 **정본 계약에 없는 경로다**(서버 `tools/contract_check.AHEAD_OF_CONTRACT`) — 실서버 `openapi.json` 은 경로만 있고
 *    응답 스키마가 비어 있다(`Any`). 그래서 모양은 서버 코드(`app/views/templates_v2.py` · `services/recipe_v2/templates.py` ·
 *    `db/migrations/0033` · `0035` · dev2 f2c9670)에서 옮겼다. 손으로 지은 칸은 없다.
 * 🔴 **입력 칸과 그 형은 목록 응답이 정한다**(`fields` · `required` · `types`) — 화면이 칸 이름을 다시 적지 않는다.
 *    서버 원문 「칸 목록을 함께 낸다 — 화면이 입력 칸을 여기서 편다(칸 이름을 화면에 다시 적지 않는다)」.
 * ⚠ 조회는 역할 무관(인증만) — viewer 도 읽는다. 쓰기는 operator/admin(`commands/templates.ts`).
 */

/** 템플릿 네 종류 — 서버 `KindName` 그대로 */
export const TEMPLATE_KINDS = ['labware', 'reagent', 'work', 'deck'] as const
export type TemplateKind = (typeof TEMPLATE_KINDS)[number]

/**
 * 템플릿 한 행 — 표의 열 그대로(`<key>` · `fields` 칸들 · `status` · `version` · `created_by` · `created_at` · `updated_at`).
 * 🔴 칸 이름이 종류마다 달라 이름으로 선언하지 않는다 — 어떤 칸이 있는지는 목록의 `key` · `fields` 가 말한다.
 */
export type TemplateRow = Readonly<Record<string, unknown>> & {
  readonly status?: unknown
  readonly version?: unknown
}

/** 목록 응답 — `{items, key, fields, required, types}`(서버 `list_items` 원문) */
export interface TemplateList {
  items: readonly TemplateRow[]
  /** 식별자 칸 이름(`labware_id` · `reagent_id` · `template_id`) */
  key: string
  /** 사람이 쓰는 칸 — 등록 · 수정 본문이 싣는 것(이 밖의 칸을 보내면 422 「모르는 칸」) */
  fields: readonly string[]
  /** 비면 안 되는 칸(`fields` 의 부분) */
  required: readonly string[]
  /** 글자가 아닌 칸의 형(`json` · `number`). 없는 칸 = 글자 */
  types: Readonly<Record<string, string>>
}

export interface TemplateListArgs {
  kind: TemplateKind
  /** 비활성도 받는다(`include_disabled=true`) */
  includeDisabled?: boolean
  /** 받지 않는다(목 데이터 · 부가 조회) */
  skip?: boolean
}

export const useTemplates = createQuery<TemplateListArgs, TemplateList>({
  key: ({ kind, includeDisabled = false }) => keys.templates.list({ kind, includeDisabled }),
  fetch: ({ kind, includeDisabled = false }) =>
    orchestrator.request<TemplateList>({
      method: 'GET',
      path: `/templates/${kind}`,
      ...(includeDisabled ? { query: { include_disabled: true } } : {}),
    }),
})

/**
 * 표준 레시피(새 형식 · Opentrons protocol/8) 한 벌 — 편집기 「표준 레시피에서」가 출발점으로 받는다.
 * 서버 원문 「본문 = `standard_recipe.json` 그대로 · 읽기 역할 전부 · DB 무접촉」.
 */
export type RecipeV2Doc = Record<string, unknown>

export const useStandardRecipeV2 = createQuery<{ skip?: boolean }, RecipeV2Doc>({
  key: () => keys.templates.detail('recipes-standard'),
  fetch: () => orchestrator.request<RecipeV2Doc>({ method: 'GET', path: '/recipes/standard' }),
})

/**
 * 레시피(새 형식) 요약 — `POST /recipes/summary`(본문 = 문서 그대로 · 선행 `AH-145`). 「레시피 템플릿」 판이 읽는다(2026-10-12).
 * 모양은 dev2 실응답(2026-10-12)에서 옮겼다 — `stages` 는 염색 작업 단계(원심 · 어셈블러 공정은 문서의 `process` 쪽이라 여기 없다).
 * 🔴 DB 를 건드리지 않는 계산이라 조회로 둔다(쓰기 아님 · `command_id` 없음).
 */
export interface RecipeV2Summary {
  stages: readonly {
    stage: string
    name: string
    shape: string
    reagent_role: string | null
    params: Readonly<Record<string, unknown>>
  }[]
  samples: readonly { sample_ref: string; wells: readonly string[] }[]
  labware: readonly { labwareId: string; displayName: string }[]
  commands: { total: number; setup: number; work: number }
  annotations: number
  violations: readonly string[]
}

export const useRecipeV2Summary = createQuery<
  {
    doc: RecipeV2Doc | undefined
    /**
     * 🔴 캐시 이름 — 이 문서가 누구인가(`standard` · 레시피 `<recipe_id>@<version>`). 종전에는 키가 고정이라 레시피마다 부르면
     *    다른 문서의 요약이 섞였다(2026-10-12 · 레시피 상세 요약을 붙이며 고침). 같은 이름 = 같은 문서여야 한다
     */
    cacheId: string
    skip?: boolean
  },
  RecipeV2Summary
>({
  key: ({ cacheId }) => keys.templates.detail(`recipe-summary:${cacheId}`),
  fetch: ({ doc }) =>
    orchestrator.request<RecipeV2Summary>({ method: 'POST', path: '/recipes/summary', body: doc }),
  enabled: ({ doc }) => doc !== undefined,
})
